import { useState, useEffect, useRef, useCallback } from 'react';
import {
  collection, query, where, onSnapshot,
  updateDoc, writeBatch, doc,
} from 'firebase/firestore';
import { db, saveToken } from '../firebase';

/**
 * Manages in-app + native browser notifications for the current user.
 *
 * Delivery layers:
 *  1. Firestore onSnapshot — real-time delivery while the tab is open.
 *  2. Notification API — native browser toast even when tab is minimized /
 *     in the background (but the tab must be open somewhere).
 *  3. FCM + Cloud Functions — true background push when the tab is closed.
 *     The firebase-messaging-sw.js service worker is already wired up for
 *     this; it just needs a Cloud Functions deploy to send the messages.
 */
export function useNotifications() {
  const userId = localStorage.getItem('user_id');

  const [notifications, setNotifications] = useState([]);
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );

  // Keep permission in a ref so showBrowserNotification closure is always fresh
  const permissionRef = useRef(permission);
  const initialLoadDone = useRef(false);

  useEffect(() => {
    permissionRef.current = permission;
  }, [permission]);

  // ─── Native browser notification ─────────────────────────────────────────
  const showBrowserNotification = useCallback((notif) => {
    if (permissionRef.current !== 'granted') return;
    // Don't interrupt the user if the tab is already focused
    if (document.hasFocus()) return;

    try {
      const n = new Notification(
        `Havenly · ${notif.from_name || 'Someone'}`,
        {
          body: notif.message || '',
          icon: `${process.env.PUBLIC_URL}/havenly-192.png`,
          badge: `${process.env.PUBLIC_URL}/havenly-50.png`,
          tag: notif.id, // deduplicate same notification
        }
      );
      n.onclick = () => {
        window.focus();
        if (notif.url) {
          window.location.href = /^https?:\/\//.test(notif.url)
            ? notif.url
            : `${process.env.PUBLIC_URL}${notif.url}`;
        }
        n.close();
      };
    } catch (e) {
      // Notification constructor can throw in some browsers; just ignore
      console.warn('Could not show browser notification:', e);
    }
  }, []);

  // ─── Register FCM token whenever permission is already granted on mount ─────
  useEffect(() => {
    if (permission === 'granted' && userId) {
      saveToken(userId).catch(() => {});
    }
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Request permission (must be called from a user gesture) ─────────────
  const requestPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') return 'denied';
    const result = await Notification.requestPermission();
    setPermission(result);
    if (result === 'granted') {
      saveToken(userId).catch(() => {});
    }
    return result;
  }, [userId]);

  // ─── Firestore real-time listener ────────────────────────────────────────
  useEffect(() => {
    if (!userId) return;
    initialLoadDone.current = false;

    const q = query(
      collection(db, 'notifications'),
      where('to_user_id', '==', userId)
      // No orderBy here to avoid needing a composite Firestore index.
      // We sort client-side below.
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notifs = snapshot.docs
        .map(d => ({
          id: d.id,
          ...d.data(),
          // Convert Firestore Timestamp → JS Date for easy rendering
          created_at: d.data().created_at?.toDate?.() || new Date(0),
        }))
        // Newest first, capped at 50
        .sort((a, b) => b.created_at - a.created_at)
        .slice(0, 50);

      // On every update AFTER the initial load, check for new docs
      if (initialLoadDone.current) {
        snapshot.docChanges().forEach(change => {
          if (change.type === 'added') {
            const notif = {
              id: change.doc.id,
              ...change.doc.data(),
              created_at: change.doc.data().created_at?.toDate?.() || new Date(0),
            };
            showBrowserNotification(notif);
          }
        });
      } else {
        initialLoadDone.current = true;
      }

      setNotifications(notifs);
    });

    return () => {
      unsubscribe();
      initialLoadDone.current = false;
    };
  }, [userId, showBrowserNotification]);

  // ─── Mark as read ─────────────────────────────────────────────────────────
  const markAsRead = useCallback(async (notificationId) => {
    await updateDoc(doc(db, 'notifications', notificationId), { read: true });
    setNotifications(prev =>
      prev.map(n => (n.id === notificationId ? { ...n, read: true } : n))
    );
  }, []);

  const markAllAsRead = useCallback(async () => {
    const unread = notifications.filter(n => !n.read);
    if (!unread.length) return;
    const batch = writeBatch(db);
    unread.forEach(n =>
      batch.update(doc(db, 'notifications', n.id), { read: true })
    );
    await batch.commit();
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, [notifications]);

  const unreadCount = notifications.filter(n => !n.read).length;

  return {
    notifications,
    unreadCount,
    permission,
    requestPermission,
    markAsRead,
    markAllAsRead,
  };
}
